import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../FirebaseConfig';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { Search, X, ChevronLeft, ChevronRight, Tag, Award, ShoppingCart, Store, GraduationCap, SlidersHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import noImg from "../assets/ChatGPT Image May 4, 2025, 05_02_10 AM.png";
import * as THREE from 'three';

// ────────────────────── HELPERS ──────────────────────

const shuffleArray = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const isNewProduct = (createdAt) => {
    if (!createdAt?.seconds) return false;
    const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000;
    return createdAt.seconds * 1000 > threeDaysAgo;
};

const contains = (str, q) =>
    str ? str.toLowerCase().includes(q.toLowerCase()) : false;

// ────────────────────── FUZZY "DID YOU MEAN?" ──────────────────────

// Levenshtein distance — measures how different two words are
const levenshtein = (a, b) => {
    const m = a.length, n = b.length;
    const dp = Array.from({ length: m + 1 }, (_, i) =>
        Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );
    for (let i = 1; i <= m; i++)
        for (let j = 1; j <= n; j++)
            dp[i][j] = a[i - 1] === b[j - 1]
                ? dp[i - 1][j - 1]
                : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    return dp[m][n];
};

// Finds the closest matching product names when nothing is found
const getFuzzySuggestions = (query, products, maxResults = 4) => {
    if (!query.trim() || products.length === 0) return [];
    const lower = query.toLowerCase();
    const seen = new Set();
    const scored = [];

    for (const p of products) {
        const name = p.name?.toLowerCase() || '';
        const store = p.storeName?.toLowerCase() || '';
        const uni = p.university?.toLowerCase() || '';

        for (const candidate of [name, store, uni]) {
            if (!candidate || seen.has(candidate)) continue;
            seen.add(candidate);

            // Check if any word in candidate is close enough
            const words = candidate.split(/\s+/);
            const queryWords = lower.split(/\s+/);
            let minDist = Infinity;
            for (const qw of queryWords) {
                for (const cw of words) {
                    if (cw.length < 2) continue;
                    const dist = levenshtein(qw, cw);
                    // Only consider it a match if distance is within 40% of word length
                    if (dist <= Math.max(1, Math.floor(qw.length * 0.4))) {
                        minDist = Math.min(minDist, dist);
                    }
                }
            }
            if (minDist < Infinity) {
                scored.push({ label: p.name || p.storeName, dist: minDist });
            }
        }
    }

    // Sort by closeness and deduplicate
    const uniqueLabels = new Set();
    return scored
        .sort((a, b) => a.dist - b.dist)
        .filter(s => {
            if (uniqueLabels.has(s.label)) return false;
            uniqueLabels.add(s.label);
            return true;
        })
        .slice(0, maxResults)
        .map(s => s.label);
};

// ────────────────────── BLACK SPACE ANIMATION ──────────────────────

const setupThreeJS = (canvasRef) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const sizes = {
        width: window.innerWidth,
        height: window.innerHeight,
    };

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, sizes.width / sizes.height, 0.1, 2000);
    camera.position.z = 1;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false });
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(1);
    renderer.setClearColor(0x000000, 0); // Transparent background

    // ── Stars (static layer, bright white) ──
    const starCount = 700;
    const starPositions = new Float32Array(starCount * 3);
    const starSizes = new Float32Array(starCount);
    for (let i = 0; i < starCount; i++) {
        starPositions[i * 3]     = (Math.random() - 0.5) * 2000;
        starPositions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
        starPositions[i * 3 + 2] = (Math.random() - 0.5) * 1500 - 200;
        starSizes[i] = Math.random() * 1.5 + 0.3;
    }
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const starMat = new THREE.PointsMaterial({
        color: 0xffffff, size: 1.0, sizeAttenuation: true,
        transparent: true, opacity: 0.75,
    });
    scene.add(new THREE.Points(starGeo, starMat));

    // ── Shooting stars / drifting dust (slow drift, very dim) ──
    const dustCount = 150;
    const dustPositions = new Float32Array(dustCount * 3);
    const dustVelocities = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
        dustPositions[i * 3]     = (Math.random() - 0.5) * 1800;
        dustPositions[i * 3 + 1] = (Math.random() - 0.5) * 1800;
        dustPositions[i * 3 + 2] = (Math.random() - 0.5) * 800;
        dustVelocities[i * 3]     = (Math.random() - 0.5) * 0.015;
        dustVelocities[i * 3 + 1] = (Math.random() - 0.5) * 0.015;
        dustVelocities[i * 3 + 2] = 0;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3));
    const dustMat = new THREE.PointsMaterial({
        color: 0xaaaaaa, size: 0.6, sizeAttenuation: true,
        transparent: true, opacity: 0.35,
    });
    const dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    let animationFrameId;
    let frame = 0;

    const animate = () => {
        frame++;

        // Slowly drift dust
        for (let i = 0; i < dustCount; i++) {
            dustPositions[i * 3]     += dustVelocities[i * 3];
            dustPositions[i * 3 + 1] += dustVelocities[i * 3 + 1];
            if (Math.abs(dustPositions[i * 3])     > 900) dustPositions[i * 3]     = (Math.random() - 0.5) * 600;
            if (Math.abs(dustPositions[i * 3 + 1]) > 900) dustPositions[i * 3 + 1] = (Math.random() - 0.5) * 600;
        }
        dustGeo.attributes.position.needsUpdate = true;

        // Very subtle camera breathe
        camera.position.x = Math.sin(frame * 0.0003) * 0.5;
        camera.position.y = Math.cos(frame * 0.0002) * 0.5;

        // Twinkle: vary star opacity gently every 90 frames
        if (frame % 90 === 0) {
            starMat.opacity = 0.6 + Math.random() * 0.3;
        }

        renderer.render(scene, camera);
        animationFrameId = requestAnimationFrame(animate);
    };

    const onResize = () => {
        sizes.width  = window.innerWidth;
        sizes.height = window.innerHeight;
        camera.aspect = sizes.width / sizes.height;
        camera.updateProjectionMatrix();
        renderer.setSize(sizes.width, sizes.height);
    };
    window.addEventListener('resize', onResize);
    animate();

    return () => {
        window.removeEventListener('resize', onResize);
        cancelAnimationFrame(animationFrameId);
        renderer.dispose();
    };
};

// ────────────────────── SEARCH BAR ──────────────────────

const SearchBar = ({ allProducts, onSearch }) => {
    const [inputVal, setInputVal] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const buildSuggestions = useCallback((val) => {
        if (!val.trim()) { setSuggestions([]); return; }
        const lower = val.toLowerCase();
        const seen = new Set();
        const results = [];

        for (const p of allProducts) {
            // Now also matches storeName and university
            if (
                contains(p.name, lower) ||
                contains(p.description, lower) ||
                contains(p.storeName, lower) ||
                contains(p.university, lower)
            ) {
                const label = p.name;
                if (label && !seen.has(label.toLowerCase())) {
                    seen.add(label.toLowerCase());
                    results.push({ label, type: 'product' });
                }
                // Add store suggestion
                if (p.storeName && !seen.has('store:' + p.storeName.toLowerCase())) {
                    seen.add('store:' + p.storeName.toLowerCase());
                    if (contains(p.storeName, lower)) {
                        results.push({ label: p.storeName, type: 'store' });
                    }
                }
                // Add university suggestion
                if (p.university && !seen.has('uni:' + p.university.toLowerCase())) {
                    seen.add('uni:' + p.university.toLowerCase());
                    if (contains(p.university, lower)) {
                        results.push({ label: p.university, type: 'university' });
                    }
                }
                if (results.length >= 7) break;
            }
        }
        setSuggestions(results);
    }, [allProducts]);

    const handleChange = (e) => {
        const val = e.target.value;
        setInputVal(val);
        buildSuggestions(val);
        setOpen(true);
    };

    const commitSearch = (val) => {
        const finalVal = val !== undefined ? val : inputVal;
        setInputVal(finalVal);
        setSuggestions([]);
        setOpen(false);
        onSearch(finalVal);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') commitSearch();
    };

    const handleClear = () => {
        setInputVal('');
        setSuggestions([]);
        setOpen(false);
        onSearch('');
    };

    const typeIcon = (type) => {
        if (type === 'store') return <Store size={13} className="text-emerald-400 shrink-0" />;
        if (type === 'university') return <GraduationCap size={13} className="text-blue-400 shrink-0" />;
        return <Search size={13} className="text-gray-500 shrink-0" />;
    };

    const typeLabel = (type) => {
        if (type === 'store') return <span className="text-[10px] text-emerald-400 ml-auto">Store</span>;
        if (type === 'university') return <span className="text-[10px] text-blue-400 ml-auto">University</span>;
        return null;
    };

    return (
        <div ref={containerRef} className="relative mb-8 w-full max-w-2xl mx-auto z-50">
            <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                    <Search className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                    <input
                        type="text"
                        placeholder="Search products, stores, universities..."
                        className="w-full p-4 pl-12 pr-10 rounded-full bg-white/5 text-white outline-none border border-white/10 backdrop-blur-md focus:border-emerald-400/60 transition-all shadow-lg text-sm sm:text-base placeholder-gray-600"
                        value={inputVal}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => inputVal && setOpen(true)}
                    />
                    {inputVal && (
                        <button onClick={handleClear} className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                            <X size={18} />
                        </button>
                    )}
                </div>
                <button
                    onClick={() => commitSearch()}
                    className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-4 px-6 rounded-full shadow-lg hover:shadow-emerald-500/30 transition-all hidden sm:block text-sm"
                >
                    Search
                </button>
            </div>

            <AnimatePresence>
                {open && suggestions.length > 0 && (
                    <motion.ul
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="absolute z-50 mt-2 w-full bg-gray-950/98 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
                    >
                        {suggestions.map((s, i) => (
                            <li
                                key={i}
                                onMouseDown={() => commitSearch(s.label)}
                                className="flex items-center gap-2 px-4 py-3 text-sm text-gray-200 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/5 last:border-0"
                            >
                                {typeIcon(s.type)}
                                <span>{s.label}</span>
                                {typeLabel(s.type)}
                            </li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
};

// ────────────────────── DID YOU MEAN? ──────────────────────

const DidYouMean = ({ query, products, onSuggest }) => {
    const suggestions = useMemo(() => getFuzzySuggestions(query, products), [query, products]);

    if (!suggestions.length) return (
        <div className="col-span-full text-center py-20 text-gray-500">
            <p className="text-5xl mb-4">🌌</p>
            <p className="text-lg font-bold text-white mb-1">Nothing found in the galaxy</p>
            <p className="text-sm">We couldn't find anything matching <span className="text-gray-300">"{query}"</span></p>
        </div>
    );

    return (
        <div className="col-span-full text-center py-16">
            <p className="text-4xl mb-4">🔍</p>
            <p className="text-lg font-bold text-white mb-1">No exact results for <span className="text-gray-400">"{query}"</span></p>
            <p className="text-sm text-gray-500 mb-6">Did you mean any of these?</p>
            <div className="flex flex-wrap gap-3 justify-center">
                {suggestions.map((s, i) => (
                    <button
                        key={i}
                        onClick={() => onSuggest(s)}
                        className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-200 hover:bg-emerald-500/20 hover:border-emerald-500/40 hover:text-white transition-all"
                    >
                        {s}
                    </button>
                ))}
            </div>
        </div>
    );
};

// ────────────────────── ADVANCED FILTER PANEL ──────────────────────

const FilterPanel = ({ products, filters, onChange }) => {
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const buttonRef = useRef(null);
    const panelRef = useRef(null);

    // Close when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleToggle = () => {
        if (!open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const panelWidth = Math.min(300, window.innerWidth - 32);
            // Align right edge of panel with right edge of button, but clamp left >= 16px
            let leftPos = rect.right - panelWidth;
            if (leftPos < 16) leftPos = 16;
            setPanelStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: leftPos,
                width: panelWidth,
                zIndex: 9999,
            });
        }
        setOpen(v => !v);
    };

    const universities = useMemo(() => {
        const set = new Set();
        products.forEach(p => { if (p.university) set.add(p.university); });
        return ['All Universities', ...Array.from(set).sort()];
    }, [products]);

    const storeNames = useMemo(() => {
        const set = new Set();
        products.forEach(p => { if (p.storeName) set.add(p.storeName); });
        return ['All Stores', ...Array.from(set).sort()];
    }, [products]);

    const hasActiveFilters = filters.university !== 'All Universities' || filters.storeName !== 'All Stores';

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={handleToggle}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                    hasActiveFilters
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                        : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
            >
                <SlidersHorizontal size={15} />
                Filters
                {hasActiveFilters && (
                    <span className="bg-emerald-500 text-white text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center">
                        {(filters.university !== 'All Universities' ? 1 : 0) + (filters.storeName !== 'All Stores' ? 1 : 0)}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        ref={panelRef}
                        initial={{ opacity: 0, y: 8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        style={panelStyle}
                        className="bg-gray-900 border border-white/10 rounded-2xl shadow-2xl p-5 backdrop-blur-xl"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-bold text-white">Advanced Filters</h3>
                            <div className="flex items-center gap-3">
                                {hasActiveFilters && (
                                    <button
                                        onClick={() => onChange({ university: 'All Universities', storeName: 'All Stores' })}
                                        className="text-[11px] text-red-400 hover:text-red-300 transition-colors"
                                    >
                                        Clear all
                                    </button>
                                )}
                                <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* University Filter */}
                        <div className="mb-4">
                            <label className="flex items-center gap-1.5 text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wider">
                                <GraduationCap size={13} className="text-blue-400" />
                                University
                            </label>
                            <div className="flex flex-col gap-1 max-h-36 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                                {universities.map(uni => (
                                    <button
                                        key={uni}
                                        onClick={() => onChange({ ...filters, university: uni })}
                                        className={`text-left px-3 py-2 rounded-xl text-xs transition-all ${
                                            filters.university === uni
                                                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                        }`}
                                    >
                                        {uni}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Store Name Filter */}
                        <div>
                            <label className="flex items-center gap-1.5 text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wider">
                                <Store size={13} className="text-emerald-400" />
                                Store
                            </label>
                            <div className="flex flex-col gap-1 max-h-36 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                                {storeNames.map(store => (
                                    <button
                                        key={store}
                                        onClick={() => onChange({ ...filters, storeName: store })}
                                        className={`text-left px-3 py-2 rounded-xl text-xs transition-all ${
                                            filters.storeName === store
                                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                        }`}
                                    >
                                        {store}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// ────────────────────── VERIFIED SELLERS STRIP ──────────────────────

const VerifiedSellerStrip = ({ products, sellerStatuses }) => {
    const scrollRef = useRef(null);

    const sellers = useMemo(() => {
        if (!products?.length) return [];
        const map = {};
        products.forEach(p => {
            if (sellerStatuses[p.sellerId] === 'approved') {
                if (!map[p.sellerId]) {
                    map[p.sellerId] = { id: p.sellerId, storeName: p.storeName || 'Verified Store', university: p.university, images: [] };
                }
                if (map[p.sellerId].images.length < 3 && p.imageUrl) map[p.sellerId].images.push(p.imageUrl);
            }
        });
        return Object.values(map).slice(0, 10);
    }, [products, sellerStatuses]);

    const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });

    if (sellers.length === 0) return null;

    return (
        <div className="mb-10">
            <div className="flex items-center gap-3 mb-4 px-1">
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5">
                    <Award size={16} className="text-emerald-400" />
                    <span className="text-emerald-300 font-bold text-sm tracking-wide">Verified Sellers</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-emerald-500/20 to-transparent" />
            </div>
            <div className="relative group">
                <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-10 h-10 rounded-full bg-gray-950 border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-xl hover:bg-white/5">
                    <ChevronLeft size={20} className="text-white" />
                </button>
                <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-10 h-10 rounded-full bg-gray-950 border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-xl hover:bg-white/5">
                    <ChevronRight size={20} className="text-white" />
                </button>
                <div ref={scrollRef} className="flex gap-4 overflow-x-auto scroll-smooth pb-4 px-2" style={{ scrollbarWidth: 'none' }}>
                    {sellers.map((seller) => (
                        <div key={seller.id} className="flex-shrink-0 w-64 h-56 bg-white/5 rounded-2xl p-4 flex flex-col justify-between transition-colors">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-bold text-white text-lg truncate">{seller.storeName}</h3>
                                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                </div>
                                <p className="text-xs text-gray-500 mb-3 truncate">{seller.university ? `📍 ${seller.university}` : 'Trusted Seller'}</p>
                                <div className="flex gap-2 mb-3">
                                    {seller.images.map((img, i) => (
                                        <div key={i} className="w-12 h-12 rounded-lg bg-gray-900 overflow-hidden">
                                            <img src={img} alt="Product" className="w-full h-full object-cover opacity-70" loading="lazy" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <Link to={`/store/${seller.id}`} className="w-full text-center bg-white/5 hover:bg-emerald-500 hover:text-white text-gray-400 text-sm font-semibold py-2 rounded-xl transition-all">
                                Visit Store
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ────────────────────── UNDER 5K DEALS ──────────────────────

const BudgetStrip = ({ products }) => {
    const scrollRef = useRef(null);

    const budgetProducts = useMemo(() => products.filter(p => {
        const price = parseFloat(String(p.price).replace(/,/g, ''));
        return !isNaN(price) && price < 5000;
    }).slice(0, 15), [products]);

    const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });

    if (budgetProducts.length === 0) return null;

    return (
        <div className="mb-10">
            <div className="flex items-center gap-3 mb-4 px-1">
                <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-4 py-1.5">
                    <Tag size={16} className="text-yellow-400" />
                    <span className="text-yellow-300 font-bold text-sm tracking-wide">Top Deals Under ₦5,000</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-yellow-500/20 to-transparent" />
            </div>
            <div className="relative group">
                <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-10 h-10 rounded-full bg-gray-950 border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-xl">
                    <ChevronLeft size={20} className="text-white" />
                </button>
                <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-10 h-10 rounded-full bg-gray-950 border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-xl">
                    <ChevronRight size={20} className="text-white" />
                </button>
                <div ref={scrollRef} className="flex gap-4 overflow-x-auto scroll-smooth pb-4 px-2" style={{ scrollbarWidth: 'none' }}>
                    {budgetProducts.map((product) => (
                        <Link key={product.id} to={`/product/${product.id}`} className="flex-shrink-0 w-44 bg-white/5 rounded-2xl p-3 flex flex-col group/card transition-colors">
                            <div className="relative w-full aspect-square rounded-xl overflow-hidden mb-3">
                                <img src={product.imageUrl || noImg} alt={product.name} className="object-cover w-full h-full group-hover/card:scale-110 transition-transform" loading="lazy" />
                                <div className="absolute top-2 right-2 bg-yellow-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full shadow">
                                    ₦{Number(String(product.price).replace(/,/g, '')).toLocaleString()}
                                </div>
                            </div>
                            <h3 className="text-xs font-semibold text-white mb-1 truncate">{product.name}</h3>
                            <p className="text-[10px] text-gray-500 truncate mb-2">{product.storeName || 'Unknown store'}</p>
                            <span className="mt-auto text-center w-full bg-yellow-500/10 text-yellow-400 text-[11px] font-bold py-1.5 rounded-lg">View Deal</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ────────────────────── MAIN PAGE ──────────────────────

const ProductPage = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [shuffledProducts, setShuffledProducts] = useState([]);
    const [sellerStatuses, setSellerStatuses] = useState({});

    const words = ["Phones", "Laptops", "Gadgets", "Sneakers", "Top Deals"];
    const [wordIndex, setWordIndex] = useState(0);

    const [sortBy, setSortBy] = useState('newest');
    const [activeCategory, setActiveCategory] = useState('all');
    const [committedSearch, setCommittedSearch] = useState('');
    const [advancedFilters, setAdvancedFilters] = useState({
        university: 'All Universities',
        storeName: 'All Stores',
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const productsPerPage = 24;

    const canvasRef = useRef(null);

    // Animated heading word
    useEffect(() => {
        const interval = setInterval(() => setWordIndex(i => (i + 1) % words.length), 2500);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const cleanup = setupThreeJS(canvasRef);
        return cleanup;
    }, []);

    // 1. Fetch Products
    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const q = query(collection(db, 'Products'), where('approved', '==', true));
                const snapshot = await getDocs(q);
                const fetched = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setAllProducts(fetched);
                setShuffledProducts(shuffleArray(fetched));
            } catch (err) {
                console.error('Error fetching products:', err);
            }
            setLoading(false);
        };
        fetchProducts();
    }, []);

    // 2. Fetch Seller Statuses
    useEffect(() => {
        if (shuffledProducts.length === 0) return;
        const CACHE_KEY = 'sellerStatuses';
        const fetchSellerStatuses = async () => {
            try {
                const cached = localStorage.getItem(CACHE_KEY);
                if (cached) {
                    const { statuses, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < 10 * 60 * 1000) { setSellerStatuses(statuses); return; }
                }
            } catch { localStorage.removeItem(CACHE_KEY); }

            const sellerIds = [...new Set(shuffledProducts.map(p => p.sellerId).filter(Boolean))];
            const results = await Promise.all(
                sellerIds.map(async (sellerId) => {
                    try {
                        const docSnap = await getDoc(doc(db, 'profiles', sellerId));
                        return [sellerId, docSnap.exists() ? docSnap.data().verification_status : 'unverified'];
                    } catch { return [sellerId, 'unverified']; }
                })
            );
            const statuses = Object.fromEntries(results);
            try { localStorage.setItem(CACHE_KEY, JSON.stringify({ statuses, timestamp: Date.now() })); } catch {}
            setSellerStatuses(statuses);
        };
        fetchSellerStatuses();
    }, [shuffledProducts]);

    // 3. Filter products — now includes university and storeName filters
    const filteredProducts = useMemo(() => {
        return shuffledProducts.filter(product => {
            // Search across name, description, storeName, AND university
            const matchesSearch = !committedSearch || (
                contains(product.name, committedSearch) ||
                contains(product.description, committedSearch) ||
                contains(product.storeName, committedSearch) ||
                contains(product.university, committedSearch)
            );

            const matchesCategory = activeCategory === 'all' ||
                (activeCategory === 'new'  && product.condition === 'New') ||
                (activeCategory === 'used' && product.condition === 'Used');

            const matchesUniversity = advancedFilters.university === 'All Universities' ||
                product.university === advancedFilters.university;

            const matchesStore = advancedFilters.storeName === 'All Stores' ||
                product.storeName === advancedFilters.storeName;

            return matchesSearch && matchesCategory && matchesUniversity && matchesStore;
        }).sort((a, b) => {
            if (sortBy === 'price_asc')  return (a.price || 0) - (b.price || 0);
            if (sortBy === 'price_desc') return (b.price || 0) - (a.price || 0);
            if (sortBy === 'newest')     return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
            return 0;
        });
    }, [shuffledProducts, committedSearch, activeCategory, sortBy, advancedFilters]);

    const paginatedProducts = filteredProducts.slice((page - 1) * productsPerPage, page * productsPerPage);
    const hasNextPage = page * productsPerPage < filteredProducts.length;
    const noResults = !loading && filteredProducts.length === 0 && committedSearch;

    const handlePagination = (direction) => {
        setPage(prev => Math.max(1, prev + direction));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleAdvancedFilterChange = (newFilters) => {
        setAdvancedFilters(newFilters);
        setPage(1);
    };

    // Active filter pill labels for display
    const activeFilterPills = [
        advancedFilters.university !== 'All Universities' && { key: 'university', label: advancedFilters.university },
        advancedFilters.storeName  !== 'All Stores'       && { key: 'storeName',  label: advancedFilters.storeName },
    ].filter(Boolean);

    return (
        <div className="relative min-h-screen text-white bg-slate-950">
            <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0 opacity-40 pointer-events-none" />

            <div className="relative z-10 p-4 sm:p-6 pt-10 max-w-7xl mx-auto">

                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-3xl sm:text-5xl font-bold text-white mb-2 flex flex-col sm:flex-row items-center justify-center gap-2">
                        <span className="text-gray-200">Looking for</span>
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-300 inline-block min-w-[180px] text-center sm:text-left">
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={wordIndex}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -20, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="inline-block"
                                >
                                    {words[wordIndex]}?
                                </motion.span>
                            </AnimatePresence>
                        </span>
                    </h1>
                    <p className="text-gray-500 text-lg">Whatever you want, <strong className="text-emerald-400">we get am!</strong></p>
                </div>

                <SearchBar
                    allProducts={allProducts}
                    onSearch={(val) => { setCommittedSearch(val); setPage(1); }}
                />

                {/* Active filter pills */}
                {activeFilterPills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4 justify-center">
                        {activeFilterPills.map(pill => (
                            <span key={pill.key} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300">
                                {pill.key === 'university' ? <GraduationCap size={12} className="text-blue-400" /> : <Store size={12} className="text-emerald-400" />}
                                {pill.label}
                                <button
                                    onClick={() => handleAdvancedFilterChange({ ...advancedFilters, [pill.key]: pill.key === 'university' ? 'All Universities' : 'All Stores' })}
                                    className="text-gray-600 hover:text-white ml-0.5"
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Strips — only when not searching */}
                {!committedSearch && !loading && (
                    <>
                        <BudgetStrip products={shuffledProducts} />
                        <VerifiedSellerStrip products={shuffledProducts} sellerStatuses={sellerStatuses} />
                    </>
                )}

                {/* Filters Row */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex flex-wrap gap-2">
                        {[
                            { key: 'all',  label: 'All'       },
                            { key: 'new',  label: '🆕 New'    },
                            { key: 'used', label: '♻️ Used'   },
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => { setActiveCategory(key); setPage(1); }}
                                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                                    activeCategory === key
                                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                                        : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/8'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Advanced Filter Panel */}
                        <FilterPanel
                            products={shuffledProducts}
                            filters={advancedFilters}
                            onChange={handleAdvancedFilterChange}
                        />

                        {/* Sort Dropdown */}
                        <div className="relative">
                            <select
                                value={sortBy}
                                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                                className="appearance-none bg-white/5 border border-white/10 text-gray-300 text-sm px-4 py-2 pr-8 rounded-full focus:outline-none focus:border-emerald-400/50 cursor-pointer"
                            >
                                <option value="newest"     className="bg-gray-950">🕒 Newest</option>
                                <option value="price_asc"  className="bg-gray-950">₦ Low → High</option>
                                <option value="price_desc" className="bg-gray-950">₦ High → Low</option>
                            </select>
                            <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 rotate-90 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Search result count */}
                {committedSearch && !loading && (
                    <div className="flex items-center gap-2 mb-6">
                        <span className="text-gray-500 text-sm">
                            Results for <strong className="text-white">"{committedSearch}"</strong>
                        </span>
                        <span className="bg-white/5 border border-white/10 text-gray-400 text-xs px-2 py-1 rounded-md">
                            {filteredProducts.length} item{filteredProducts.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                )}

                {/* Product Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                    {loading ? (
                        <div className="col-span-full flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full border-2 border-t-2 border-emerald-500 border-t-transparent w-10 h-10" />
                        </div>
                    ) : noResults ? (
                        <DidYouMean
                            query={committedSearch}
                            products={allProducts}
                            onSuggest={(val) => { setCommittedSearch(val); setPage(1); }}
                        />
                    ) : paginatedProducts.length > 0 ? (
                        paginatedProducts.map((product) => {
                            const isVerified = sellerStatuses[product.sellerId] === 'approved';
                            return (
                                <Link
                                    key={product.id}
                                    to={`/product/${product.id}`}
                                    className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden flex flex-col group hover:border-emerald-500/50 transition-all hover:-translate-y-1"
                                >
                                    <div className="relative w-full aspect-square bg-gray-900 overflow-hidden">
                                        <img
                                            src={product.imageUrl || noImg}
                                            alt={product.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-2">
                                            <span className="text-white font-bold">₦{Number(product.price).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="p-3 flex flex-col flex-grow">
                                        <h3 className="text-sm font-semibold text-white truncate mb-1">{product.name}</h3>
                                        <p className="text-[11px] text-gray-400 line-clamp-2 mb-3 flex-grow">{product.description}</p>

                                        <div className="flex items-center justify-between mt-auto mb-3">
                                            <span className="text-[10px] text-gray-500 truncate max-w-[60%]">{product.storeName}</span>
                                            {isVerified && <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded">Verified</span>}
                                        </div>

                                        <button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold py-2 rounded-xl flex items-center justify-center gap-1 transition-colors">
                                            <ShoppingCart size={14} /> Buy Now
                                        </button>
                                    </div>
                                </Link>
                            );
                        })
                    ) : (
                        <div className="col-span-full text-center py-20 text-gray-600">
                            <p className="text-4xl mb-4">📭</p>
                            <p className="text-lg font-bold text-white mb-1">No products here</p>
                            <p className="text-sm">Try adjusting your filters.</p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loading && filteredProducts.length > productsPerPage && (
                    <div className="flex justify-center items-center mt-12 gap-4">
                        <button
                            className="bg-white/5 border border-white/10 px-5 py-2 rounded-full font-semibold text-gray-300 hover:bg-white/10 transition-all disabled:opacity-30 text-sm"
                            onClick={() => handlePagination(-1)}
                            disabled={page === 1}
                        >
                            ← Prev
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {page} of {Math.ceil(filteredProducts.length / productsPerPage)}
                        </span>
                        <button
                            className="bg-emerald-500 hover:bg-emerald-400 px-5 py-2 rounded-full font-semibold text-white shadow-lg shadow-emerald-500/20 disabled:opacity-30 transition-all text-sm"
                            onClick={() => handlePagination(1)}
                            disabled={!hasNextPage}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductPage;